import { mapActions, mapGetters, mapMutations } from 'vuex';
import isEmpty from 'lodash/isEmpty';

import { CreditCardPaymentConfig, TAB } from 'aa/vue/FormConfig';
import { ButtonState } from 'models/ButtonConfig';
import { AA_DYNAMIC_AGREEMENT_COPY } from 'helpers/featureConstants';
import {
    FTC_COMPLIANCE_ENABLED,
    FTC_COMPLIANCE_SWITCHPLAN_ENABLED,
    WAIVE_COOLING_RIGHTS_ENABLED,
    SINGLE_COLUMN_PAYMENT_PAGE,
    REMOVE_COUPON_ON_SWITCH_PLAN,
} from 'aa/helpers/featureConstants';
import PaymentService from 'aa/vue/services/PaymentService';
import NotificationBarModel from 'aa/vue/models/NotificationBarModel';
import { PAGE_TYPES, ACTION_NAMES, APP_LOG_TYPE, tracking } from 'services/Tracking';
import {
    PAGE_TYPES as AA_PAGE_TYPES,
    ACTION_NAMES as AA_ACTION_NAMES,
} from 'aa/vue/constants/aaTrackingConstants';
import { FLOW_TYPE } from 'models/FlowModel';
import Domain from 'helpers/Domain';
import { DateFormatter, getEndDate, getDateAfterExtendedFreeTrial } from 'aa/helpers/DateFormatter';
import Product from 'aa/vue/models/Product';
import { PAYMENT_CC, PAYMENT_PAYPAL, SUBSCRIPTION_ALREADY_IN_USE } from 'aa/vue/constants/shared';
import commonMixins from 'aa/vue/plugins/account/commonMixins';
import priceMixin from 'aa/vue/plugins/priceMixin';
import aarpMixin from 'aa/vue/plugins/aarpMixin';
import { RECAPTCHA_FORMS } from 'recaptcha/Recaptcha';
import {
    checkForCouponErrors,
    onCouponErrored,
    getTemporalAmount,
} from 'aa/vue/helpers/coupon.helpers';

export default {
    data() {
        return {
            recurlyWrappersLoaded: false,
            recurlyReady: false,
            pricing: null,
            coupon: {},
            threeDSecureEnabled: false,
            internationalizedPrice: '',
            internationalizedPriceWithUnit: '',
            couponDisclaimerCopy: '',
            trialEndDateFormatted: '',
            appliedCouponCode: this.$store.state.promo,
            registrationCountry: false,
            submittedCouponCode: undefined,
            enhancedPaymentSummaryEnabled: false,
            nextNonZeroBillingPrice: '',
            nextNonZeroBillingPriceUnformatted: '',
            waiveOfCoolingChecked: false,
            waiveOfCoolingRightsEnabled: false,
            waiveOfCoolingRightsCheckboxErrorMessage: '',
            shouldRecomputeSubmitState: false,
            recaptchaAction: '',
            pricingAttached: false,
        };
    },
    mixins: [commonMixins, priceMixin, aarpMixin],
    computed: {
        ...mapGetters('user', ['isExSubscriber', 'isSubscriber', 'isRegistered']),

        ...mapGetters(['bundleShowtime', 'featureIsActive']),

        ...mapGetters('plan', ['getSelectedPlan', 'isSwitchPlan']),

        ...mapGetters({
            upgradeAnnualToggle: 'featureFlags/upgradeAnnualToggle',
            plan: 'plan/getSelectedPlan',
        }),
        ...mapGetters('user', ['isNoTrialUser']),

        ...mapGetters('ftc', [
            'getEditingStoredPaymentMethod',
            'getPaymentMethod',
            'getFormValidated',
            'getFormErrorCode',
            'getTransactionProcessing',
            'getCoolingRightsWaived',
            'getPaymentMethodUpdateProcessing',
            'getPaymentMethodUpdateComplete',
            'getTransactionComplete',
            'getSubmitButtonState',
            'getPayPalToken',
            'getFieldConfigs',
        ]),

        ...mapGetters('flow', {
            flowType: 'type',
            isNoFreeTrialCampaign: 'isNoFreeTrialCampaign',
            isCampaign: 'isCampaign',
            isGiftFlow: 'isGift',
            isEdu: 'isEdu',
        }),
        isFTCComplianceEnabled() {
            return this.$store.getters.featureIsActive(FTC_COMPLIANCE_ENABLED);
        },
        isFTCComplianceSwitchPlanEnabled() {
            return this.$store.getters.featureIsActive(FTC_COMPLIANCE_SWITCHPLAN_ENABLED);
        },
        getSummaryAgreementCopy() {
            return this.enhancedPaymentSummaryEnabled
                ? this.pageAttr.billing_summary_agreement_copy
                : this.pageAttr.aa_payment_agreement_copy;
        },
        couponCheckPending() {
            return Boolean(this.appliedCouponCode && !this?.$store?.state?.autoPromoChecked);
        },
        recurly() {
            return this.$store.state.payment.recurly;
        },
        needsWaiveOfCoolingRights() {
            return (
                this.enhancedPaymentSummaryEnabled &&
                this.waiveOfCoolingRightsEnabled &&
                Boolean(this.pageAttr?.['waive_of_cooling_rights']) &&
                !this.isEditPayment
            );
        },
        waiveOfCoolingRightsCheckboxConfig() {
            return {
                type: 'checkbox',
                elementId: 'waiveCoolingRights',
                inputLabel: this.pageAttr['waive_of_cooling_rights'],
                wrapperClass:
                    'payment-layout__summary__waive-cooling-rights cbs-checkbox cbs-checkbox--waive-cooling-rights',
                styleClass: 'qt-waiveCoolingRights',
                classList:
                    'payment-layout__summary__waive-cooling-rights__checkbox cbs-checkbox-input',
                value: false,
            };
        },
        buttonConfig() {
            let buttonEnabledText = '';
            if (this.isFTCComplianceEnabled) {
                buttonEnabledText = this.pageAttr.billing_summary_cta_form_submit_ftc;
            } else {
                buttonEnabledText = this.enhancedPaymentSummaryEnabled
                    ? this.pageAttr.billing_summary_cta_form_submit
                    : this.pageAttr.cta_form_submit;
            }

            if (!this.isEditPayment) {
                return {
                    type: 'button',
                    displayTexts: {
                        [ButtonState.Loading]: this.$getLocale('_processing'),
                        [ButtonState.Enabled]: buttonEnabledText,
                    },
                    id: 'payment-submit',
                    styleClass: 'payment-layout__summary__submit button',
                    qaClass: 'qt-AAbtn',
                };
            }
            return {
                displayText: this?.isFromPaymentSummary
                    ? this.pageAttr.update_cta
                    : this.$getLocale('save'),
                id: 'payment-submit',
                styleClass: [
                    'payment-layout__summary__submit button',
                    this?.editPaymentSuccess ? 'success' : '',
                ],
            };
        },
        submitButtonState() {
            if (
                typeof this.tab !== 'undefined' &&
                this.activeTab === this.tab.payPal &&
                !this.needsWaiveOfCoolingRights
            ) {
                return ButtonState.Enabled;
            }

            if (this.formIsProcessing) {
                return ButtonState.Loading;
            }

            if (
                this.needsWaiveOfCoolingRights &&
                !this.isEditPayment &&
                !this.isFromPaymentSummary
            ) {
                if (
                    this.activeTab === this.tab.cc &&
                    (!this.formIsValid || !this.waiveOfCoolingChecked)
                ) {
                    return ButtonState.Disabled;
                }
                if (this.activeTab === this.tab.payPal && !this.waiveOfCoolingChecked) {
                    return ButtonState.Disabled;
                }
            } else if (!this.formIsValid || this.formErrorCode === SUBSCRIPTION_ALREADY_IN_USE) {
                return ButtonState.Disabled;
            }

            return ButtonState.Enabled;
        },
        bundlePageAttrSuffix() {
            if (this.bundleShowtime) {
                return '_showtime';
            }

            return '';
        },
        dataRecurlyFieldKeyMap() {
            const localFieldConfigs = this.isFTCComplianceEnabled
                ? this.getFieldConfigs
                : this.fieldConfigs;
            const fieldConfigs = isEmpty(localFieldConfigs)
                ? this.getFieldConfigs
                : localFieldConfigs;

            return this.dataRecurlyFieldKeyMapHelper(fieldConfigs ?? {});
        },
        pageAttr() {
            let pageAttributes;

            if (this.isEditPayment) {
                pageAttributes = this.$store.state.payment.pageAttributes;
                if (this.isFromPaymentSummary && !this.$store.getters['user/isExSubscriber']) {
                    pageAttributes.update_cta =
                        this.$store.state.AccountSwitchPlan.pageAttributes.update_cta ||
                        this.$store.state.AccountSwitchPlan.pageAttributes.aa_edit_payment_method;
                } else if (this.$store.getters['user/isExSubscriber']) {
                    pageAttributes.update_cta =
                        this.$store.state.pageAttributes.attributes.update_cta || '';
                }
            } else if (
                this.$router.currentRoute.name === 'AccountSummary' ||
                this.$router.currentRoute.name === 'AccountSummaryFTC' ||
                this.$router.currentRoute.name === 'directAccountSummary'
            ) {
                pageAttributes = this.$store.state.AccountSwitchPlan.pageAttributes;
            } else {
                pageAttributes = this.$store.state.pageAttributes.attributes;
            }

            if (this.bundleShowtime) {
                return Object.assign({}, pageAttributes, {
                    aa_payment_paypal_success_copy:
                        pageAttributes[
                            `aa_payment_paypal_success_copy${this.bundlePageAttrSuffix}`
                        ],
                    aa_payment_agreement_copy:
                        pageAttributes[`aa_payment_agreement_copy${this.bundlePageAttrSuffix}`],
                    aa_payment_agreement_copy_monthly:
                        pageAttributes[
                            `aa_payment_agreement_copy_monthly${this.bundlePageAttrSuffix}`
                        ],
                    aa_payment_agreement_copy_annual:
                        pageAttributes[
                            `aa_payment_agreement_copy_annual${this.bundlePageAttrSuffix}`
                        ],
                    cta_form_submit: pageAttributes[`cta_form_submit${this.bundlePageAttrSuffix}`],
                });
            }

            return pageAttributes;
        },

        trackingService() {
            return this.$trackingService ?? tracking;
        },
    },
    methods: {
        ...mapActions('payment', [
            'retrieveRecurlyToken',
            'retrieveRecurlyPayPalToken',
            'processPayment',
            'processEditPayment',
        ]),
        ...mapMutations('ftc', [
            'setPaymentMethod',
            'setFormValidated',
            'setTransactionProcessing',
            'setConnectedToPayPal',
            'setCoolingRightsWaived',
            'setFormErrorCode',
            'setPaymentMethodUpdateProcessing',
            'setTransactionComplete',
            'setPaymentMethodUpdateComplete',
            'setPaypalToken',
        ]),
        ...mapActions('ftc', [
            'processPurchaseWithRecurlyToken',
            'processPurchaseWithPaypalToken',
            'processPurchaseWithStoredPaymentMethod',
            'updateStoredPaymentMethodInSwitchPlan',
            'retrieveRecurlyFormToken',
        ]),
        /**
         * shared proxy to updateSubTotal() to alleviate duplicate code
         * TODO: consider refactor to other usages of updateSubTotal
         */
        formatOrderSummaryTotals() {
            const { currencySymbol, currencyCode } = this.recurly.Pricing.Subscription();
            this.updateSubTotal(currencySymbol, currencyCode);
            this.getNextNonZeroBillingPrice();
        },
        updateSubTotal(currencySymbol, currencyCode) {
            const subtotals = document.querySelectorAll('[data-recurly^="subtotal"]');

            subtotals.forEach((i) => {
                i.textContent = this.pricePrettyPrint(i.textContent, currencySymbol, currencyCode);
            });
        },
        displayCouponErrorMessage(message) {
            this.coupon = {
                errorMessage: this.$getLocale(message),
            };

            // clear any coupon disclaimer
            this.couponDisclaimerCopy = '';
        },
        normalizePriceValue(price) {
            if (price == null || price === '') {
                return null;
            }

            return String(price).replace(/,/g, '');
        },
        isRecurlyPricingStaleForSelectedPlan(billingPeriod) {
            const selectedPlanUnitPrice = this.normalizePriceValue(this.plan?.rawPrice);
            const recurlyPlanUnitPrice = this.normalizePriceValue(
                this.pricing?.price?.[billingPeriod]?.plan,
            );

            if (!selectedPlanUnitPrice || !recurlyPlanUnitPrice) {
                return false;
            }

            return selectedPlanUnitPrice !== recurlyPlanUnitPrice;
        },
        getNextNonZeroBillingPrice() {
            let nextCostToUser = this.internationalizedPrice;
            let found = false;

            if (this.pricing?.price) {
                if (this.pricing.price.now?.subtotal) {
                    if (
                        parseFloat(this.pricing.price.now.subtotal) > 0 &&
                        !this.isRecurlyPricingStaleForSelectedPlan('now')
                    ) {
                        nextCostToUser = this.pricing.price.now.subtotal;
                        found = true;
                    }
                }
                if (this.pricing.price.next?.subtotal && !found) {
                    if (
                        parseFloat(this.pricing.price.next.subtotal) > 0 &&
                        !this.isRecurlyPricingStaleForSelectedPlan('next')
                    ) {
                        nextCostToUser = this.pricing.price.next.subtotal;
                        found = true;
                    }
                }
            }
            if (found) {
                this.nextNonZeroBillingPriceUnformatted = nextCostToUser;
                nextCostToUser = this.pricePrettyPrint(
                    nextCostToUser,
                    this.pricing.currencySymbol,
                    this.pricing.currencyCode,
                );
            }
            this.nextNonZeroBillingPrice = nextCostToUser;
        },

        configureRecurly() {
            if (this.recurly) {
                const currentPlan = this.plan || {};
                this.recurly.configure({
                    ...CreditCardPaymentConfig.recurlyConfig,
                    currency: currentPlan.currency,
                    risk: {
                        threeDSecure: {
                            preflightDeviceDataCollector: this.threeDSecureEnabled ?? false,
                        },
                    },
                });
                this.recurlyReady = true;
                !this.isEditPayment && this.setupPricingHandlers();
            } else if (this.isEditPayment) {
                this.openNotification(
                    new NotificationBarModel({
                        success: false,
                        message: this.$getLocale('something_went_wrong_please_try_again_later'),
                    }),
                );
            }
        },
        dataRecurlyFieldKeyMapHelper(fieldConfigs) {
            const result = {};

            Object.keys(fieldConfigs).forEach((fieldKey) => {
                let fieldConfig = fieldConfigs[fieldKey];

                if (fieldConfig.dataRecurly) {
                    result[fieldConfig.dataRecurly] = fieldKey;
                }
            });

            return result;
        },
        getAgreementCopy() {
            if (!this.plan || this.isFTCComplianceEnabled) {
                return '';
            }
            if (this.featureDynamicAgreementCopy) {
                let copy = this.plan.isMonthly
                    ? this.pageAttr.aa_payment_agreement_copy_monthly
                    : this.pageAttr.aa_payment_agreement_copy_annual;

                const price = this.plan.price;
                const disclaimerCopy = copy.replace(/\[PRICE\]/gi, price);

                return disclaimerCopy;
            } else {
                let agreement_copy = this.getSummaryAgreementCopy ?? '';

                if (agreement_copy.length !== 0) {
                    const { planType } = this.plan;
                    let localizedSubPeriod = '';
                    if (planType === Product.TYPE_MONTHLY) {
                        localizedSubPeriod = this.$getLocale('month');
                    } else if (planType === Product.TYPE_ANNUAL) {
                        localizedSubPeriod = this.$getLocale('year');
                    }

                    if (this.internationalizedPrice) {
                        agreement_copy = agreement_copy.replace(
                            /{subscriptionPrice}/g,
                            this.internationalizedPrice,
                        );
                    }
                    if (this.internationalizedPriceWithUnit) {
                        agreement_copy = agreement_copy.replace(
                            /{subscriptionPriceWithUnit}/g,
                            this.internationalizedPriceWithUnit,
                        );
                    }

                    agreement_copy = agreement_copy.replace(
                        /{subscriptionPeriod}/g,
                        localizedSubPeriod,
                    );
                    agreement_copy = agreement_copy.replace(/{(\w)*}/g, '');
                }

                return agreement_copy;
            }
        },
        updatePrice(e) {
            this.internationalizedPrice = e.internationalizedPrice;
            this.internationalizedPriceWithUnit = e.internationalizedPriceWithUnit;
            this.nextNonZeroBillingPriceUnformatted = e.internationalizedPriceUnformatted;
            this.getNextNonZeroBillingPrice();
        },
        /**
         *
         * @param {Object} coupon - The coupon object from Recurly
         */
        handleCouponChange(coupon, fromPlanChange = false) {
            const { currencySymbol, currencyCode } = this.recurly.Pricing.Subscription();
            const { code } = coupon;
            if (code) {
                const split = code.split('-');
                if (split.length >= 2 && split[split.length - 1].length === 2) {
                    coupon.regionCode = split.pop().toLowerCase();
                }
            }

            const checkError = checkForCouponErrors(
                coupon,
                this.registrationCountry
                    ? this.registrationCountry.toUpperCase()
                    : this.registrationCountry,
                this.pricing.price.currency.code,
                this.isExSubscriber,
            );
            if (checkError) {
                if (fromPlanChange) {
                    this.resetCoupon();
                }
                if (code.toLowerCase() !== this?.appliedCouponCode?.toLowerCase()) {
                    this.displayCouponErrorMessage(onCouponErrored(checkError));
                } else {
                    this.$store.commit('setAutoPromoChecked', true);
                }
            } else {
                let couponInfo = {
                    recurlyInfo: coupon,
                };
                if (code.toLowerCase() === this?.appliedCouponCode?.toLowerCase()) {
                    this.$store.commit('setAutoPromoChecked', true);
                }
                if (this.formatCouponString) {
                    if (coupon.discount.type === 'free_trial') {
                        couponInfo.trialString = this.formatCouponString(
                            coupon,
                            currencySymbol,
                            currencyCode,
                            this.plan?.planType,
                        );
                    } else {
                        couponInfo.discountString = this.formatCouponString(
                            coupon,
                            currencySymbol,
                            currencyCode,
                            this.plan?.planType,
                        );
                    }
                }
                this.coupon = couponInfo;
            }
            this.formatOrderSummaryTotals();
        },

        setupPricingHandlers() {
            if (this.recurly) {
                const currentPlan = this.plan;
                let pricing = this.recurly.Pricing();

                if (!this.isFTCComplianceEnabled && !this.isFTCComplianceSwitchPlanEnabled) {
                    pricing.attach(this.$refs.paymentForm);
                    this.pricingAttached = true;
                }
                if (!this.isEditPayment) {
                    let _self = this;
                    const { currencySymbol, currencyCode } = this.recurly.Pricing.Subscription();
                    pricing.on('change', () => {
                        // adding delay to ensure price change is applied before formatting
                        this.$nextTick(() => {
                            _self.formatOrderSummaryTotals();
                        });
                    });

                    // adding listener for annual plan upsell
                    pricing.on('set.plan', () => {
                        this.coupon.errorMessage = '';
                        this.formatOrderSummaryTotals();
                    });
                    _self.updateSubTotal(currencySymbol, currencyCode);
                    pricing.on('set.coupon', (coupon) => {
                        this.handleCouponChange(coupon);
                    });

                    pricing.on('error.coupon', (err) => {
                        if (
                            !this.submittedCouponCode ||
                            this.submittedCouponCode !== this.$store.state.promo
                        ) {
                            this.displayCouponErrorMessage(onCouponErrored(err));
                        } else {
                            this.coupon = {};
                            this.$store.commit('setAutoPromoChecked', true);
                        }
                    });
                }

                this.pricing = pricing;

                // Apply coupon
                const removeCouponOnSwitchPlan = this.featureIsActive(REMOVE_COUPON_ON_SWITCH_PLAN);

                const removeSubscriptionPromo = this.isSwitchPlan && removeCouponOnSwitchPlan;
                const promo =
                    this.$store?.state?.promo ||
                    (removeSubscriptionPromo ? '' : this?.currentSubscriptionPromo);

                if (promo) {
                    this.applyCoupon({
                        plan: currentPlan.recurlyCode,
                        couponCode: promo,
                    });
                }
            }
        },
        applyCoupon(event) {
            let { recurlyInfo } = this.coupon;
            if (!event.couponCode) {
                this.resetCoupon();
            }

            if (!recurlyInfo || recurlyInfo?.code !== event.couponCode) {
                this.submittedCouponCode = event.couponCode;
            }
            this.applyCouponAndPlanToPricing(event.plan, event.couponCode);
        },
        applyCouponAndPlanToPricing(plan, couponCode) {
            // switch-plan downgrades drop URL promos but must still apply the plan; signup never downgrades
            const suppressUrlPromo =
                this.isSwitchPlan &&
                this.getSelectedPlan?.switchDirection === 'downgrade' &&
                !this.$store.getters.getServerDataByKey('isCMSPromo');

            // PPIWEBM-2885: when the flag strips the carried-over account coupon during switch
            // plan, remove it from the Recurly pricing too (not just the display) so first
            // payment + legal copy reflect the SKU price. A URL ?promo=, FOREVER, or
            // user-applied coupon sets shouldRemoveSwitchPlanCoupon=false and is applied normally.
            const removeCoupon = this.shouldRemoveSwitchPlanCoupon === true || suppressUrlPromo;
            const effectiveCouponCode = removeCoupon ? '' : couponCode;
            if (removeCoupon) {
                this.resetCoupon();
            }

            if (this.pricing) {
                this.pricing
                    .plan(plan)
                    .coupon(effectiveCouponCode)
                    .then(() => {
                        // a suppressed URL promo stays in state so a later upgrade can reuse it
                        if (!suppressUrlPromo) {
                            this.$store.commit('setPromo', effectiveCouponCode);
                        }
                    })
                    .catch(() => {})
                    .done(() => {
                        this.formatOrderSummaryTotals();
                    });
            }
        },
        updateOrder(order, shouldReset = true) {
            if (this.recurly) {
                this.appliedCouponCode = order.couponCode;

                if (shouldReset) {
                    this.resetCoupon();
                }
                this.couponDisclaimerCopy = '';

                if (!this.pricingAttached) {
                    const paymentForm =
                        this.$store.state.payment.formReference || this.$refs.paymentForm;
                    this.pricing.attach(paymentForm);
                    this.pricingAttached = true;
                }
                this.applyCouponAndPlanToPricing(order.plan, order.couponCode);
            }
        },
        resetCoupon(event) {
            this.coupon = {};
        },
        updateTrialEndDate(trialEndDateFormatted) {
            this.trialEndDateFormatted = trialEndDateFormatted;
        },
        mapRecurlyErrorFields(recurlyFieldsArr) {
            const errorMap = {};
            recurlyFieldsArr.forEach((dataRecurly) => {
                const fieldKey = this.dataRecurlyFieldKeyMap[dataRecurly];
                errorMap[fieldKey] = this.$getLocale(`invalid_${fieldKey}`);
            });

            this.setFieldErrors(errorMap);
            this.formIsValid = false;
        },
        clearNotificationMessage() {
            if (
                this.$store.state.notification.isOpen &&
                !this.$store.state.notification.isClosing
            ) {
                this.$store.dispatch('notification/close');
            }
        },
        onSubmitPurchaseFTC(e) {
            if (!this.getCoolingRightsWaived) {
                this.setWaiveOfCoolingRightsErrorMessage();
                return false;
            }

            if (this.getPaymentMethod === this.tab.cc) {
                // const paymentForm = this.$store.state.payment.formReference;
                // this.retrieveRecurlyToken(paymentForm);
                this.handleSubmit(e);
                return true;
            }

            if (this.getPaymentMethod === this.tab.payPal) {
                const payPalToken = this.$store.state.payment.payPalToken || this.getPayPalToken;
                if (!payPalToken) {
                    return false;
                }

                if (this.isEditPayment) {
                    this.submitEditPayment(payPalToken);
                } else {
                    this.submitPayment(payPalToken, e);
                }
                return true;
            }

            return false;
        },
        onClickSubmit(e) {
            if (this.formIsProcessing) {
                return false;
            }

            const waive_of_cooling_rights_checked = this.isFTCComplianceEnabled
                ? this.getCoolingRightsWaived
                : this.waiveOfCoolingChecked;

            if (this.activeTab === this.tab.cc) {
                if (this.needsWaiveOfCoolingRights && !waive_of_cooling_rights_checked) {
                    this.setWaiveOfCoolingRightsErrorMessage();
                    // This if statement is intended for reuse validationForm method
                    // already available inside the handleSubmit function without
                    // duplicating the call to the validationForm method
                    if (!this.formIsValid) {
                        this.handleSubmit(e);
                    }
                } else {
                    if (this.isEditPayment && !this.isSwitchPlan) {
                        this.setPaymentMethodUpdateProcessing(true);
                    }
                    this.handleSubmit(e);
                }
            } else {
                const payPalToken = this.isFTCComplianceEnabled
                    ? this.getPayPalToken
                    : this.payPalToken;

                if (!payPalToken) {
                    return false;
                }

                if (this.needsWaiveOfCoolingRights && !waive_of_cooling_rights_checked) {
                    this.setWaiveOfCoolingRightsErrorMessage();
                } else {
                    if (this.isEditPayment) {
                        if (!this.isSwitchPlan) {
                            this.setPaymentMethodUpdateProcessing(true);
                        }
                        this.submitEditPayment(payPalToken);
                    } else {
                        this.submitPayment(payPalToken, e);
                    }
                }
            }
        },
        submitExSubscriberPayment(e) {
            // Will this not work with promos
            this.submitPayment(null, e);
        },
        handleValidForm(e) {
            // Submit with click coordinates only
            if (!(e instanceof Event)) {
                return false;
            }

            if (this.activeTab === this.tab.cc) {
                this.formIsProcessing = true;

                let self = this;

                const localRef = this.$refs.paymentForm;
                const storeRef = this.$store.state.payment.formReference;
                const paymentFormRef = this.isFTCComplianceEnabled
                    ? storeRef
                    : localRef instanceof HTMLFormElement
                      ? localRef
                      : storeRef || localRef;

                this.retrieveRecurlyToken(paymentFormRef)
                    .then((token) => {
                        if (token.id) {
                            if (this.isEditPayment) {
                                this.submitEditPayment(token);
                            } else {
                                this.submitPayment(token, e);
                            }
                        } else {
                            // TODO: display payment er
                        }
                    })
                    .catch((rejectVal) => {
                        this.formIsProcessing = false;
                        this.setTransactionProcessing(false);

                        if (this.isExSubscriber) {
                            tracking.trackAction(ACTION_NAMES.APP_LOG, {
                                appLogText: rejectVal.message || 'No message provided',
                                appLogType: APP_LOG_TYPE.UPDATE_PAYMENT_FORM_ERROR,
                                appLogCode: 'No code provided',
                            });
                        }

                        try {
                            let recurlyErrorFields = rejectVal.error.fields;
                            if (recurlyErrorFields.length > 0) {
                                this.mapRecurlyErrorFields(recurlyErrorFields);
                                this?.scrollToFirstFieldError?.();
                            }
                        } catch (error) {
                            console.log(error);
                        }
                    });
            }
        },
        handlePayPalCheckout(e, paypalGateway = false) {
            e.preventDefault();

            let trackingActionName = this.isEditPayment
                ? ACTION_NAMES.SWITCH_TO_PAYPAL
                : ACTION_NAMES.PAYPAL_CHECKOUT_BUTTON;

            let trackingParams = this.isEditPayment
                ? {
                      pageType: AA_PAGE_TYPES.ACCOUNT_SWITCH_PAYMENT,
                      payPalCheckoutClick: '1',
                  }
                : { ctaText: 'checkout paypal' };

            if (this.isFTCComplianceEnabled && this.isFTCComplianceSwitchPlanEnabled) {
                trackingActionName = ACTION_NAMES.PAYPAL_CHECKOUT_BUTTON;
                trackingParams.pageType = PAGE_TYPES.PLAN_CHANGE_CONFIRMATION;
                trackingParams.ctaText = 'switch to paypal';
            }

            if (this.isExSubscriber) {
                trackingActionName = ACTION_NAMES.PAYPAL_CHECKOUT_BUTTON;
                trackingParams.pageType = AA_PAGE_TYPES.EXSUB_PAYMENT;
                trackingParams.ctaText = 'switch to paypal';
            }

            this.trackingService.trackAction(trackingActionName, trackingParams);

            let self = this;

            this.retrieveRecurlyPayPalToken(paypalGateway)
                .then((token) => {
                    self.$store.commit('ftc/setPayPalToken', token);
                    self.payPalToken = token;
                    this.isEditPayment && self.onClickSubmit();
                })
                .catch((rejectVal) => {});
        },
        resubmitPayment(token) {
            const payload = {
                ...this.paymentRequestData,
                resultActionTokenId: token.id,
            };
            const httpData = this.isEditPayment ? this.buildHttpData(payload) : payload;
            this.sendPayment(httpData);
        },
        submitEditPayment(token) {
            const payload = {
                token: token.id,
            };

            payload.recaptchaAction = RECAPTCHA_FORMS.FORM_EDIT_PAYMENT;

            this.paymentRequestData = payload;
            const httpData = this.buildHttpData(payload);
            this.sendPayment(httpData);
        },

        submitPayment(token, buttonEvent) {
            this.formErrorMessage = null;
            this.formIsProcessing = true;

            let couponCode = null;
            if (this.coupon && this.coupon.recurlyInfo && this.coupon.recurlyInfo.code) {
                couponCode = this.coupon.recurlyInfo.code;
            }

            const payload = {
                token: token,
                buttonEvent: buttonEvent,
                plan: this.plan,
                couponCode: couponCode,
                tk_trp: this.$store.state.token,
            };

            // requires AARP MIXIN
            if (this.flowType === FLOW_TYPE.AARP) {
                let aarpUserId = { aarpUserId: this.getAarpUserId() };
                Object.assign(payload, aarpUserId);
            }

            // Save payload for retry if needed
            this.paymentRequestData = payload;

            this.isEditPayment &&
                this.trackingService.trackAction(ACTION_NAMES.CREDIT_CARD_SUBMIT_UPDATE);
            this.sendPayment(payload);
        },
        sendPayment(payload) {
            this.formIsProcessing = true;

            payload.recaptchaAction =
                this.hasOwnProperty('recaptchaAction') &&
                this.recaptchaFormActionEnabled(this.recaptchaAction)
                    ? this.recaptchaAction
                    : RECAPTCHA_FORMS.FORM_PAYMENT;

            const couponCode = payload.couponCode;
            if ('URLSearchParams' in window) {
                if (location.search) {
                    const searchParams = new URLSearchParams(location.search.substr(1));
                    const gatewayOverride = searchParams.get('gateway');
                    if (gatewayOverride) {
                        payload.gateway = gatewayOverride;
                    }
                }
            }

            const processSelectedMethod = this.isEditPayment
                ? this.processEditPayment
                : this.processPayment;
            processSelectedMethod(payload)
                .then(async (resp) => {
                    this.handleProcessedPayment(resp);
                    if (this.isRegistered || this.isExSubscriber) {
                        await this.$store.dispatch('storedPaymentMethod/updateBillingInfo');
                    }

                    if (!resp.success || !this.isEditPayment) {
                        return;
                    }

                    const purchasePaymentMethod =
                        this.activeTab === this.tab.cc ? PAYMENT_CC : PAYMENT_PAYPAL;
                    if (this.isFromPaymentSummary) {
                        await this.syncServerDataWithDeepAssign();
                        this.formIsProcessing = false;
                        this.editPaymentSuccess = true;
                        this.trackingService.trackAction(
                            ACTION_NAMES.TRACK_UPDATE_PAYMENT_METHOD_SUCCESS,
                            {
                                ctaText: this.buttonConfig.displayText,
                                pageURL: window.location.href,
                                pageType: this.isExSubscriber
                                    ? AA_PAGE_TYPES.EXSUB_PAYMENT
                                    : PAGE_TYPES.PLAN_CHANGE_CONFIRMATION,
                                purchasePaymentMethod,
                            },
                        );

                        // Timeout to allow the button success state to be displayed before closing the form in the payment summary page
                        setTimeout(() => {
                            this.$emit('update-payment-method-success', resp.success);
                            this.editPaymentSuccess = false;
                        }, 1000);

                        this.openNotification(
                            new NotificationBarModel({
                                success: true,
                                message: this.$getLocale(
                                    'your_payment_information_was_successfully_updated',
                                ),
                            }),
                        );
                    }

                    if (!this.isFromPaymentSummary) {
                        this.trackingService.trackAction(
                            ACTION_NAMES.TRACK_UPDATE_PAYMENT_METHOD_SUCCESS,
                            {
                                linkName: ACTION_NAMES.TRACK_UPDATE_PAYMENT_METHOD_SUCCESS,
                                pageType: PAGE_TYPES.ACCOUNT_UPDATE_PAYMENT,
                                pageURL: window.location.href,
                                pageName: '/account/payment',
                                purchasePaymentMethod,
                                optimizelyExp: CBS.Tracking?.utag_data?.optimizelyExp,
                                userType: this.$store.state.user?.svod?.status,
                                userStatus: this.$store.getters['user/trackingUserStatus'],
                                userProfileId: CBS.Registry?.user?.profile_id,
                                userProfileCategory: CBS.Registry?.user?.profile_type,
                                userProfilePicPath: CBS.Registry?.user?.userProfilePicPath,
                                userRegId: this.$store.state?.user?.regID,
                            },
                        );
                    }
                })
                .catch((err) => {
                    let trackingParams = {};
                    if (this.activeTab === TAB.payPal) {
                        const { planType } = this.plan;
                        trackingParams.purchasePaymentMethod = this.activeTab;
                        trackingParams.productPricingPlan = planType;
                    }
                    if (this.isExSubscriber) {
                        trackingParams.pageType = AA_PAGE_TYPES.EXSUB_PAYMENT;
                    }

                    this.handleProcessedPaymentError(err, trackingParams, this.isSubscriber);
                })
                .finally(() => {
                    this.setTransactionProcessing(false);
                    this.setPaymentMethodUpdateProcessing(false);
                    if (this.formIsProcessing) {
                        this.formIsProcessing = false;
                    }
                });
        },
        setWaiveOfCoolingRights(e) {
            if (this.showCheckbox) {
                if (!e.value && this.getCoolingRightsWaived) {
                    const pageType = () => {
                        if (this.isSwitchPlan) {
                            return PAGE_TYPES.PLAN_CHANGE_CONFIRMATION;
                        }

                        return this.isExSubscriber
                            ? AA_PAGE_TYPES.EXSUB_PAYMENT
                            : AA_PAGE_TYPES.PAYMENT;
                    };

                    this.trackingService.trackAction(AA_ACTION_NAMES.FTC_CHECKBOX_DESELECT, {
                        pageUrl: window.location.href,
                        pageType: pageType(),
                    });
                }
                this.setCoolingRightsWaived(e.value);
            } else {
                this.waiveOfCoolingChecked = e.value;
            }

            if (this.waiveOfCoolingRightsCheckboxErrorMessage) {
                this.waiveOfCoolingRightsCheckboxErrorMessage = '';
            }
            this.triggerSubmitStateReevaluation();
        },
        setWaiveOfCoolingRightsErrorMessage() {
            this.waiveOfCoolingRightsCheckboxErrorMessage =
                this.pageAttr?.['waive_of_cooling_rights_error'];
        },
        pollForPaymentRef() {
            // https://jefrydco.id/en/blog/safe-access-vue-refs-undefined#safeway
            const interval = setInterval(() => {
                if (this.$refs.paymentForm) {
                    this.recurlyWrappersLoaded = true;
                    if (this.recurly) {
                        this.configureRecurly();
                    }
                    clearInterval(interval);
                }
            }, 50);
        },

        addTrialPeriod() {
            if (
                (this.isFTC &&
                    this.isSwitchPlan &&
                    this.featureIsActive(FTC_COMPLIANCE_SWITCHPLAN_ENABLED)) ||
                this.isNoTrialUser
            ) {
                // US doesn't set `trial` to `false` when they don't want the trial plan
                return new Date();
            }
            if (this.plan.trial) {
                return getEndDate(this.plan.trialInterval, this.plan.trialUnit);
            }
            return new Date();
        },

        getFirstBillingDate() {
            return this.addTrialPeriod();
        },

        /** Returns the date to be used after the extended free trial */
        getFirstBillingDateAfterExtendedFreeTrial(coupon) {
            const { temporal_unit, temporal_amount } = coupon.recurlyInfo;
            const isPlanTypeAnnual = this.plan.planType === Product.TYPE_ANNUAL;
            const cadenceUnit = isPlanTypeAnnual ? 'year' : 'month';
            const isSingleUseCoupon = coupon?.recurlyInfo?.single_use ?? coupon?.singleUse;
            let adjustedTemporalAmount;
            let billingDate = this.getFirstBillingDate();
            if (coupon) {
                if (isSingleUseCoupon) {
                    adjustedTemporalAmount = 1;
                    billingDate = getDateAfterExtendedFreeTrial(
                        billingDate,
                        adjustedTemporalAmount,
                        cadenceUnit,
                    );
                } else {
                    // Adjust for mismatches between temporal unit and cadence unit
                    if (temporal_unit === 'month' && cadenceUnit === 'year') {
                        // Another year for every billing cycle past in months
                        adjustedTemporalAmount = Math.ceil(temporal_amount / 12);
                        billingDate = getDateAfterExtendedFreeTrial(
                            billingDate,
                            adjustedTemporalAmount,
                            cadenceUnit,
                        );
                    } else if (temporal_unit === 'year' && cadenceUnit === 'month') {
                        adjustedTemporalAmount = temporal_amount * 12;
                        billingDate = getDateAfterExtendedFreeTrial(
                            billingDate,
                            adjustedTemporalAmount,
                            cadenceUnit,
                        );
                    } else {
                        billingDate = getDateAfterExtendedFreeTrial(
                            billingDate,
                            temporal_amount,
                            temporal_unit,
                        );
                    }
                }
            }
            return DateFormatter.format(billingDate, null, CBS.Registry.region.dateLocale);
        },

        updateFirstBillingDate(coupon) {
            if (coupon?.recurlyInfo) {
                const is100PercentOffPromo =
                    coupon.recurlyInfo?.discount?.type === 'percent' &&
                    coupon.recurlyInfo?.discount?.rate === 1;

                if (is100PercentOffPromo) {
                    this.couponDiscountBillingDate =
                        this.getFirstBillingDateAfterExtendedFreeTrial(coupon);
                }
            } else {
                this.couponDiscountBillingDate = null;
            }
        },

        /* 
        Removed from SummaryFTC
        */
        calcFullCouponBillingDate(coupon, couponLength, unit) {
            const purchaseDate = this.addTrialPeriod();
            const isSingleUseCoupon = coupon?.recurlyInfo?.single_use ?? coupon?.singleUse;

            const isPlanTypeAnnual = this.plan.planType === Product.TYPE_ANNUAL;
            const planUnit = isPlanTypeAnnual ? 'year' : 'month';
            let adjustedCouponLength = getTemporalAmount(coupon, isPlanTypeAnnual, couponLength);
            if (isSingleUseCoupon) {
                adjustedCouponLength = 1;
            }

            let billingDate = purchaseDate;
            if (!!adjustedCouponLength && !!unit) {
                billingDate = getDateAfterExtendedFreeTrial(
                    purchaseDate,
                    adjustedCouponLength,
                    planUnit,
                ); // we need plan unit because we need to show the cadence aligned with the plan, not with the coupon
            }

            this.couponDiscountBillingDate = DateFormatter.format(
                billingDate,
                null,
                CBS.Registry.region.dateLocale,
            );
        },
        triggerSubmitStateReevaluation() {
            this.shouldRecomputeSubmitState = !this.shouldRecomputeSubmitState;
        },
    },
    watch: {
        recurly(newVal, oldVal) {
            if (newVal && this.recurlyWrappersLoaded) {
                this.configureRecurly();
            }
        },
        waiveOfCoolingChecked(newValue) {
            this.$emit('waive-of-cooling-checked', newValue);
        },
        getCoolingRightsWaived(newValue) {
            this.$emit('waive-of-cooling-checked', newValue);
        },
    },
    created() {
        this.registrationCountry = this.$store.state.serverData.paymentForm?.country?.value
            ? this.$store.state.serverData.paymentForm?.country?.value.toLowerCase()
            : false;
        this.threeDSecureEnabled = this.$store.state.serverData.threeDSecure;
        this.featureDynamicAgreementCopy =
            this.$store.getters.featureIsActive(AA_DYNAMIC_AGREEMENT_COPY);
        this.enhancedPaymentSummaryEnabled =
            this.$store.state.serverData.enhancedPaymentSummaryEnabled;
        this.waiveOfCoolingRightsEnabled = this.featureIsActive(WAIVE_COOLING_RIGHTS_ENABLED);

        CreditCardPaymentConfig.recurlyConfig.publicKey =
            this.$store.state.serverData.recurlyPublicKey;
    },
    mounted() {
        // add check for language
        PaymentService.setHttpService(this.$store.$http);
        this.pollForPaymentRef();
    },
    beforeDestroy() {
        // this condition is to prevent the notification from being closed
        // just after the edit payment form collapse in the summary page
        if (
            typeof this.isFromPaymentSummary === 'undefined' ||
            this.isFromPaymentSummary === false
        ) {
            this.clearNotificationMessage();
        }
    },
};
